import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Check, X } from 'lucide-react'
import { motion } from 'framer-motion'
import Wordmark from '@/components/layout/Wordmark'
import { useBrand } from '@/hooks/useBrand'

type Interval = 'monthly' | 'annual'

interface TierDef {
  slug: string
  name: string
  monthly: number
  annual: number
  mostPopular?: boolean
  promise: string
  description: string
  highlights: string[]
}

const TIERS: TierDef[] = [
  {
    slug: 'associate',
    name: 'Associate',
    monthly: 397,
    annual: 3970,
    promise: 'A full-service AI intake layer for your website.',
    description:
      "Your firm's own AI receptionist, trained on your practice areas, your website, and a knowledge base you control. Every visitor gets a tailored conversation; every qualified lead lands on your calendar. Your team sees everything in real time — jump in, take over, or let the AI handle it.",
    highlights: [
      'Tailored AI chat trained on your website + knowledge base',
      'Bookings on your calendar, live staff takeover',
      'Team management, chat history, custom alerts',
      'Analytics dashboard for the whole practice',
    ],
  },
  {
    slug: 'partner',
    name: 'Partner',
    monthly: 897,
    annual: 8970,
    mostPopular: true,
    promise: 'Intake that never sleeps — web and phone.',
    description:
      "Everything in Associate, plus your AI answers every inbound call, 24/7. No voicemail, no lost prospects, no after-hours gap. Calls are transcribed, summarized, and scored the moment they end — your team walks in each morning knowing exactly who called and what they need.",
    highlights: [
      'Everything in Associate',
      'AI answers every call, 24/7',
      'Dedicated number, 250 calls/mo',
      'Transcripts, summaries, caller history',
    ],
  },
  {
    slug: 'principal',
    name: 'Principal',
    monthly: 1497,
    annual: 14970,
    promise: 'Every inquiry becomes a conversation.',
    description:
      "Everything in Partner, plus proactive outreach. The moment a prospect submits an inquiry on your website, your AI calls them personally — turning form submissions into live conversations while the lead is still warm. If a caller needs to step away or accidentally ends a call mid-intake, your AI follows up to finish the matter. Paired with CRM sync, reminder calls, and priority support, nothing in your funnel goes unattended.",
    highlights: [
      'Everything in Partner',
      'Proactive callback on every web inquiry',
      'CRM sync (Clio, Lawmatics, HubSpot, more)',
      '600 calls/mo, reminder calls, priority support',
    ],
  },
]

const COMPARISON = [
  { feature: 'AI Chat Agent', associate: true, partner: true, principal: true },
  { feature: 'AI Inbound Agent', associate: false, partner: true, principal: true },
  { feature: 'AI Outbound Agent', associate: false, partner: false, principal: true },
  { feature: 'Phone Number', associate: '—', partner: '1 Local', principal: '1 Local' },
  { feature: 'Calls Included', associate: '—', partner: '250', principal: '600' },
  { feature: 'Chat Messages', associate: '5,000', partner: 'Unlimited', principal: 'Unlimited' },
  { feature: 'Knowledge Base', associate: true, partner: true, principal: true },
  { feature: 'Analytics', associate: true, partner: true, principal: true },
  { feature: 'Lead Scoring', associate: true, partner: true, principal: true },
  { feature: 'Booking + Calendar', associate: true, partner: true, principal: true },
  { feature: 'Human Takeover (chat)', associate: true, partner: true, principal: true },
  { feature: 'Call Transcripts', associate: false, partner: true, principal: true },
  { feature: 'Call Summaries', associate: false, partner: true, principal: true },
  { feature: 'Caller History', associate: false, partner: true, principal: true },
  { feature: 'Auto Callback', associate: false, partner: false, principal: true },
  { feature: 'Auto Follow-Up', associate: false, partner: false, principal: true },
  { feature: 'Reminder Calls', associate: false, partner: false, principal: true },
  { feature: 'Outreach Lists', associate: false, partner: false, principal: true },
  { feature: 'CRM Integrations', associate: false, partner: false, principal: true },
  { feature: 'Priority Support', associate: false, partner: false, principal: true },
]

const ADDONS = [
  { name: 'Inbound Agent', price: '$199/mo', description: 'Add inbound to an existing number' },
  { name: 'Outbound Agent', price: '$199/mo', description: 'Add outbound to an existing number' },
  { name: 'Agent Pair + Number', price: '$349/mo', description: '1 number + both agents (save $74)' },
  { name: 'Local Number', price: '$25/mo', description: 'Additional local phone number' },
  { name: 'Toll-Free Number', price: '$35/mo', description: 'Additional toll-free number' },
  { name: 'Premium Voice', price: '$99/mo', description: 'ElevenLabs Flash voice upgrade' },
]

const CALL_PACKS = [
  { calls: 10, price: 20 },
  { calls: 25, price: 45 },
  { calls: 50, price: 80 },
  { calls: 100, price: 150 },
  { calls: 250, price: 350 },
  { calls: 500, price: 650 },
]

function renderCell(value: boolean | string) {
  if (value === true) return <Check className="h-4 w-4 text-brand-periwinkle mx-auto" strokeWidth={2.5} />
  if (value === false) return <X className="h-4 w-4 text-white/20 mx-auto" strokeWidth={2.5} />
  return <span className="text-sm text-white/80">{value}</span>
}

export default function PricingPage() {
  const brand = useBrand()
  const [interval, setInterval] = useState<Interval>('monthly')

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d0a1f] to-[#12103a] text-white">
      {/* Header */}
      <header className="border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/">
            <Wordmark size="md" variant="light" />
          </Link>
          <Link to="/onboarding/step/1">
            <Button size="sm" className="bg-gradient-to-r from-brand-royal to-brand-periwinkle">
              Get started
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 lg:py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <h1 className="text-4xl lg:text-6xl font-heading font-semibold tracking-tight leading-[1.05] mb-4">
            From Associate to Principal,
            <br />
            <span className="text-white/60">the team that scales with your firm.</span>
          </h1>
          <p className="text-white/60 text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
            Every lead gets a response. Every caller speaks to someone. Instantly, 24/7.
          </p>
        </motion.div>
      </section>

      {/* Interval toggle */}
      <div className="flex items-center justify-center gap-3 mb-10">
        <button
          type="button"
          onClick={() => setInterval('monthly')}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
            interval === 'monthly' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/80'
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => setInterval('annual')}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
            interval === 'annual' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/80'
          }`}
        >
          Annual <span className="ml-2 text-[10px] text-brand-periwinkle">2 months free</span>
        </button>
      </div>

      {/* Tier cards */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-5">
          {TIERS.map((tier) => (
            <motion.div
              key={tier.slug}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: TIERS.indexOf(tier) * 0.08 }}
              className={`relative rounded-2xl border p-8 ${
                tier.mostPopular
                  ? 'border-brand-periwinkle/40 bg-gradient-to-b from-brand-royal/10 to-transparent'
                  : 'border-white/10 bg-white/[0.02]'
              }`}
            >
              {tier.mostPopular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-brand-periwinkle text-brand-royal">
                  Most Popular
                </span>
              )}
              <h3 className="text-xl font-semibold mb-2">{tier.name}</h3>
              <p className="text-sm text-brand-periwinkle mb-4 min-h-[40px]">{tier.promise}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">
                  ${interval === 'monthly' ? tier.monthly : Math.round(tier.annual / 12)}
                </span>
                <span className="text-sm text-white/50 ml-1">/mo</span>
                {interval === 'annual' && (
                  <p className="text-xs text-white/40 mt-1">${tier.annual.toLocaleString()} billed annually</p>
                )}
              </div>
              <ul className="space-y-2.5 mb-8 min-h-[140px]">
                {tier.highlights.map((h) => {
                  // Tier-colored checks to reinforce the ladder visually
                  const checkColor = tier.slug === 'associate'
                    ? 'text-sky-400'
                    : tier.slug === 'partner'
                      ? 'text-brand-periwinkle'
                      : 'text-violet-400'
                  return (
                    <li key={h} className="flex items-start gap-2 text-sm text-white/80">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${checkColor}`} strokeWidth={2.5} />
                      <span>{h}</span>
                    </li>
                  )
                })}
              </ul>
              <Link to="/onboarding/step/1" className="block">
                <Button
                  size="lg"
                  className={`w-full h-11 ${
                    tier.mostPopular
                      ? 'bg-gradient-to-r from-brand-royal to-brand-periwinkle'
                      : 'bg-white/10 hover:bg-white/15'
                  }`}
                >
                  Get started
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-xs text-white/40 mt-6">
          Month-to-month. Cancel anytime. No setup fees.
        </p>
      </section>

      {/* Tier descriptions — the paragraph copy that doesn't fit on the card */}
      <section className="px-6 py-12 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xs uppercase tracking-widest text-white/40 text-center mb-8">What each plan really does</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {TIERS.map((tier) => {
              const accent = tier.slug === 'associate'
                ? 'text-sky-400 border-sky-400/30'
                : tier.slug === 'partner'
                  ? 'text-brand-periwinkle border-brand-periwinkle/30'
                  : 'text-violet-400 border-violet-400/30'
              return (
                <div key={tier.slug} className="space-y-3">
                  <h3 className={`text-lg font-semibold border-b pb-2 ${accent}`}>{tier.name}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{tier.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Feature comparison */}
      <section className="px-6 py-12 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl lg:text-3xl font-heading font-semibold mb-8 text-center">Feature comparison</h2>
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-black/40">
                  <th className="text-left px-5 py-3 font-medium text-white/70 text-xs uppercase tracking-wider">Feature</th>
                  <th className="px-5 py-3 font-medium text-white/70 text-xs uppercase tracking-wider">Associate</th>
                  <th className="px-5 py-3 font-medium text-brand-periwinkle text-xs uppercase tracking-wider">Partner</th>
                  <th className="px-5 py-3 font-medium text-white/70 text-xs uppercase tracking-wider">Principal</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, idx) => (
                  <tr key={row.feature} className={idx % 2 === 0 ? 'bg-white/[0.02]' : ''}>
                    <td className="px-5 py-3 text-white/80">{row.feature}</td>
                    <td className="px-5 py-3 text-center">{renderCell(row.associate)}</td>
                    <td className="px-5 py-3 text-center bg-brand-royal/[0.05]">{renderCell(row.partner)}</td>
                    <td className="px-5 py-3 text-center">{renderCell(row.principal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Add-ons + Call Packs */}
      <section className="px-6 py-12 border-t border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h3 className="text-xl font-semibold mb-4">Add-ons</h3>
            <div className="rounded-2xl border border-white/10 overflow-hidden">
              {ADDONS.map((addon, idx) => (
                <div
                  key={addon.name}
                  className={`flex items-center justify-between px-5 py-3 ${
                    idx < ADDONS.length - 1 ? 'border-b border-white/5' : ''
                  } ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
                >
                  <div>
                    <p className="text-sm font-medium">{addon.name}</p>
                    <p className="text-xs text-white/50 mt-0.5">{addon.description}</p>
                  </div>
                  <span className="text-sm font-semibold text-brand-periwinkle">{addon.price}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-4">Call Packs</h3>
            <p className="text-sm text-white/60 mb-4">
              Need more calls? Buy a pack anytime or enable auto top-ups with a cap you control.
            </p>
            <div className="rounded-2xl border border-white/10 overflow-hidden">
              {CALL_PACKS.map((pack, idx) => (
                <div
                  key={pack.calls}
                  className={`flex items-center justify-between px-5 py-3 ${
                    idx < CALL_PACKS.length - 1 ? 'border-b border-white/5' : ''
                  } ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
                >
                  <span className="text-sm font-medium">{pack.calls} calls</span>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-brand-periwinkle">${pack.price}</span>
                    <span className="text-[11px] text-white/40 ml-2">
                      ${(pack.price / pack.calls).toFixed(2)}/call
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20 text-center">
        <h2 className="text-3xl lg:text-4xl font-heading font-semibold mb-4">
          Ready to never miss a lead?
        </h2>
        <p className="text-white/60 mb-8 max-w-xl mx-auto">
          Month-to-month. Cancel anytime. No setup fees, no long-term contract.
        </p>
        <Link to="/onboarding/step/1">
          <Button size="lg" className="h-12 px-8 bg-gradient-to-r from-brand-royal to-brand-periwinkle">
            Get started
          </Button>
        </Link>
      </section>

      <footer className="border-t border-white/5 px-6 py-8 text-center text-xs text-white/40">
        © {new Date().getFullYear()} {brand.appName}. All rights reserved.
      </footer>
    </div>
  )
}
